import React, { useState, useCallback } from 'react';
import { Card, Form, Row, Col, Button, Badge, Collapse, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSearch, faTimes, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

/**
 * Pannello filtri generico riutilizzabile.
 *
 * Props:
 *   fields: Array<{
 *     id: string,
 *     label: string,
 *     type: 'text' | 'select' | 'date' | 'date-range' | 'number-range',
 *     options?: string[] | {value, label}[],   // solo per type='select'
 *     placeholder?: string,
 *     colSize?: number,   // Bootstrap col md size, default 3
 *   }>
 *   onApply: (filters: object) => void   // chiamato con { id: value, ... }
 *   defaultOpen?: boolean
 *   className?: string
 *
 * Campi speciali:
 *   type='date-range' → value = { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
 *   type='number-range' → value = { min: '', max: '' }
 */
const FilterPanel = ({ fields = [], onApply, defaultOpen = false, className = '' }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [values, setValues] = useState({});

  const setValue = useCallback((id, val) => {
    setValues(prev => ({ ...prev, [id]: val }));
  }, []);

  const handleApply = useCallback(() => {
    onApply(values);
  }, [values, onApply]);

  const handleReset = useCallback(() => {
    setValues({});
    onApply({});
  }, [onApply]);

  // Applica anche premendo Enter nei campi testo/number
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleApply();
  }, [handleApply]);

  // Conta filtri attivi
  const activeCount = Object.entries(values).filter(([, v]) => {
    if (v === undefined || v === null || v === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    if (typeof v === 'object' && !Array.isArray(v)) {
      return Object.values(v).some(x => x !== undefined && x !== '' && x !== null);
    }
    return true;
  }).length;

  const renderField = (field) => {
    const val = values[field.id];

    switch (field.type) {
      case 'select':
        return (
          <Form.Select
            size="sm"
            value={val ?? ''}
            onChange={e => setValue(field.id, e.target.value || undefined)}
          >
            <option value="">Tutti</option>
            {(field.options || []).map(opt => {
              const v = typeof opt === 'object' ? opt.value : opt;
              const l = typeof opt === 'object' ? opt.label : opt;
              return <option key={v} value={v}>{l}</option>;
            })}
          </Form.Select>
        );

      case 'date':
        return (
          <Form.Control
            type="date"
            size="sm"
            value={val ?? ''}
            onChange={e => setValue(field.id, e.target.value || undefined)}
          />
        );

      case 'date-range': {
        const from = val?.from ?? '';
        const to = val?.to ?? '';
        return (
          <div className="d-flex gap-1">
            <Form.Control
              type="date" size="sm" placeholder="Da" value={from}
              onChange={e => setValue(field.id, { from: e.target.value, to })}
            />
            <Form.Control
              type="date" size="sm" placeholder="A" value={to}
              onChange={e => setValue(field.id, { from, to: e.target.value })}
            />
          </div>
        );
      }

      case 'number-range': {
        const min = val?.min ?? '';
        const max = val?.max ?? '';
        return (
          <div className="d-flex gap-1">
            <Form.Control
              type="number" size="sm" placeholder="Min" value={min}
              onChange={e => setValue(field.id, { min: e.target.value, max })}
              onKeyDown={handleKeyDown}
            />
            <Form.Control
              type="number" size="sm" placeholder="Max" value={max}
              onChange={e => setValue(field.id, { min, max: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </div>
        );
      }

      default: // text
        return (
          <InputGroup size="sm">
            <Form.Control
              type="text"
              size="sm"
              placeholder={field.placeholder || 'Cerca...'}
              value={val ?? ''}
              onChange={e => setValue(field.id, e.target.value || undefined)}
              onKeyDown={handleKeyDown}
            />
            {val && (
              <Button variant="outline-secondary" size="sm" onClick={() => setValue(field.id, undefined)}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            )}
          </InputGroup>
        );
    }
  };

  return (
    <Card className={`mb-2 border-0 shadow-sm ${className}`}>
      <Card.Header
        className="d-flex justify-content-between align-items-center py-2"
        style={{ cursor: 'pointer', background: '#f8f9fa' }}
        onClick={() => setOpen(o => !o)}
      >
        <span className="fw-semibold text-secondary">
          <FontAwesomeIcon icon={faFilter} className="me-2" />
          Filtri
          {activeCount > 0 && (
            <Badge bg="primary" pill className="ms-2">{activeCount}</Badge>
          )}
        </span>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="text-secondary" />
      </Card.Header>
      <Collapse in={open}>
        <div>
          <Card.Body className="py-3">
            <Row className="g-2 align-items-end">
              {fields.map(field => (
                <Col md={field.colSize || 3} key={field.id}>
                  <Form.Label className="small mb-1 text-muted">{field.label}</Form.Label>
                  {renderField(field)}
                </Col>
              ))}
              <Col md="auto" className="ms-auto">
                <div className="d-flex gap-2">
                  {activeCount > 0 && (
                    <Button variant="outline-secondary" size="sm" onClick={handleReset}>
                      <FontAwesomeIcon icon={faTimes} className="me-1" /> Azzera
                    </Button>
                  )}
                  <Button variant="primary" size="sm" onClick={handleApply}>
                    <FontAwesomeIcon icon={faSearch} className="me-1" /> Applica
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

export default FilterPanel;
